import { type ReactNode, memo } from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import IosShareIcon from '@mui/icons-material/IosShare'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { SONGS_LIST_STRINGS } from './strings'

interface SongsListHeaderProps {
  songsCount: number
  reviewType: string
  detailsOpen: boolean
  chatOpen: boolean
  onShowDetails: () => void
  onShowChat: () => void
  onShare?: () => void
  onClose: () => void
}

const HEADER_STYLES = {
  px: 4,
  pt: 3.5,
  bgcolor: 'background.paper',
}

const HEADER_CONTENT_STYLES = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 2,
}

const TITLE_STYLES = {
  flexGrow: 1,
  m: 0,
  fontSize: 28,
  fontWeight: 500,
}

const ACTIONS_STYLES = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const getIconButtonStyles = (isActive: boolean) => ({
  width: 48,
  height: 48,
  color: 'text.primary',
  backgroundColor: isActive ? 'action.hover' : 'transparent',
})

const HeaderTitle = memo(
  ({ songsCount, reviewType }: { songsCount: number; reviewType: string }) => (
    <Box>
      <Typography component="h2" sx={TITLE_STYLES}>
        {SONGS_LIST_STRINGS.request}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 15, color: 'text.secondary' }}>
        {[SONGS_LIST_STRINGS.songs(songsCount), reviewType].join(' · ')}
      </Typography>
    </Box>
  ),
)

const ActionButton = memo(
  ({
    icon,
    label,
    onClick,
    isActive = false,
    size = 'medium',
  }: {
    icon: ReactNode
    label: string
    onClick: () => void
    isActive?: boolean
    size?: 'small' | 'medium' | 'large'
  }) => (
    <IconButton aria-label={label} onClick={onClick} size={size} sx={getIconButtonStyles(isActive)}>
      {icon}
    </IconButton>
  ),
)

const HeaderActions = memo(
  ({
    detailsOpen,
    chatOpen,
    onShowDetails,
    onShowChat,
    onShare,
    onClose,
  }: {
    detailsOpen: boolean
    chatOpen: boolean
    onShowDetails: () => void
    onShowChat: () => void
    onShare?: () => void
    onClose: () => void
  }) => (
    <Box sx={ACTIONS_STYLES}>
      <ActionButton
        icon={<InfoOutlinedIcon />}
        label={SONGS_LIST_STRINGS.details}
        onClick={onShowDetails}
        isActive={detailsOpen}
      />
      <ActionButton
        icon={<ChatBubbleOutlineIcon />}
        label={SONGS_LIST_STRINGS.chat}
        onClick={onShowChat}
        isActive={chatOpen}
      />
      {onShare && (
        <ActionButton icon={<IosShareIcon />} label={SONGS_LIST_STRINGS.share} onClick={onShare} />
      )}
      <ActionButton icon={<CloseIcon />} label={SONGS_LIST_STRINGS.close} onClick={onClose} />
    </Box>
  ),
)

export const SongsListHeader = memo(
  ({
    songsCount,
    reviewType,
    detailsOpen,
    chatOpen,
    onShowDetails,
    onShowChat,
    onShare,
    onClose,
  }: SongsListHeaderProps) => {
    return (
      <Box sx={HEADER_STYLES}>
        <Box sx={HEADER_CONTENT_STYLES}>
          <HeaderTitle songsCount={songsCount} reviewType={reviewType} />
          <HeaderActions
            detailsOpen={detailsOpen}
            chatOpen={chatOpen}
            onShowDetails={onShowDetails}
            onShowChat={onShowChat}
            onShare={onShare}
            onClose={onClose}
          />
        </Box>
      </Box>
    )
  },
)
